import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Twitter, Youtube } from "lucide-react";
import { toast } from "sonner";
import { createEmailSignup } from "../../services/api";

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await createEmailSignup(email);
      toast.success("You're on the list. We'll holler.");
      setEmail("");
    } catch (err) {
      if (err.response?.status === 400) {
        toast.success("You're already on the list.");
        setEmail("");
      } else {
        toast.error("Couldn't sign up. Try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="bg-navy text-bone relative">
      <div className="max-w-[1600px] mx-auto px-5 md:px-12 pt-20 md:pt-28 pb-10">
        {/* Big wordmark */}
        <div className="border-b border-bone/15 pb-10 md:pb-14">
          <Link to="/" data-testid="footer-logo-link" aria-label="Home">
            <h2 className="font-display uppercase tracking-tighter leading-[0.85] text-[18vw] md:text-[14vw] text-bone hover:text-red transition-colors duration-300">
              FRDM
            </h2>
          </Link>
          <p className="font-mono uppercase tracking-[0.3em] text-[10px] md:text-xs text-bone/60 mt-3">
            ◢ FREEDOM COFFEE · YOU WERE BORN FREE · EST. 2026
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mt-10 md:mt-14">
          {/* Stay in the loop */}
          <div className="md:col-span-2">
            <p className="font-mono uppercase tracking-widest text-[10px] text-red mb-3">
              Stay in the loop
            </p>
            <form
              onSubmit={handleSubmit}
              data-testid="footer-signup-form"
              className="flex flex-col sm:flex-row gap-3 max-w-md"
            >
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                data-testid="footer-signup-input"
                className="flex-1 bg-transparent border-2 border-bone rounded-none px-4 py-3 font-mono text-sm text-bone placeholder:text-bone/40 brut-focus"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="footer-signup-submit"
                className="bg-red text-bone hover:bg-bone hover:text-red transition-colors font-display uppercase tracking-wider text-lg px-5 py-3 border-2 border-bone disabled:opacity-60"
              >
                {loading ? "Sending…" : "Notify Me"}
              </button>
            </form>
          </div>

          {/* Social */}
          <div>
            <p className="font-mono uppercase tracking-widest text-[10px] text-red mb-4">
              Social
            </p>
            <div className="flex gap-3">
              {[
                { Icon: Instagram, href: "#", testId: "footer-social-instagram" },
                { Icon: Twitter, href: "#", testId: "footer-social-twitter" },
                { Icon: Youtube, href: "#", testId: "footer-social-youtube" },
              ].map(({ Icon, href, testId }, i) => (
                <a
                  key={i}
                  href={href}
                  data-testid={testId}
                  aria-label="social"
                  className="w-11 h-11 grid place-items-center bg-bone text-navy hover:bg-red hover:text-bone transition-colors -rotate-3 hover:rotate-0"
                >
                  <Icon size={18} />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-bone/15 flex flex-col md:flex-row justify-between gap-3 font-mono uppercase tracking-widest text-[10px] text-bone/60">
          <p data-testid="footer-copyright">
            © 2026 Freedom Coffee · All rights reserved
          </p>
          <p className="flex gap-5">
            <Link
              to="/jobs"
              data-testid="footer-jobs-link"
              className="hover:text-red"
            >
              Jobs
            </Link>
            <Link
              to="/privacy"
              data-testid="footer-privacy-link"
              className="hover:text-red"
            >
              Privacy
            </Link>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
